// landing/LandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HousePortal } from '@/App'; // Assuming App.jsx exports HousePortal from your project root
import { TermsAndPrivacy } from '@/components/auth/TermsAndPrivacy'; // Assuming this path is correct
import { CheckCircle } from 'lucide-react'; // For Cookie Consent

import HeaderSection from './HeaderSection';
import HeroSection from './HeroSection';
import FeatureShowcaseSection from './FeatureShowcaseSection';

import PricingSection from './PricingSection';
import FAQsSection from './FAQsSection';
import FooterSection from './FooterSection';

/* ========================
 * LandingPage - Main component for the Shelbula landing page
 * Orchestrates all sections, manages global state like modals and cookies
 * ========================*/
export function LandingPage() {
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [activeTermsTab, setActiveTermsTab] = useState('terms');
  const [cookiesAccepted, setCookiesAccepted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cookieConsent') === 'accepted';
    }
    return false;
  });
  const [scrollPosition, setScrollPosition] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);
  
  const handleSignupClick = () => {
    navigate('/signup');
  };
  
  const handleLoginClick = () => {
    navigate('/login');
  };
  
  const acceptCookies = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookieConsent', 'accepted');
    }
    setCookiesAccepted(true);
  };

  const handleShowTerms = (tab) => {
    setActiveTermsTab(tab);
    setShowTerms(true);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white font-sans">
      {/* Global styles (ensure these are not duplicated if you have a global stylesheet) */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradientFlow 5s ease infinite;
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.5s ease-out forwards;
        }
      `}</style>

      {/* Background elements */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTkuNSA2MEgwVjBoNjBWNjBoLS41ek01OSAxSDFtMCAxaDU4TTEgM2g1OE0xIDRoNTgiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==')] opacity-30"></div>
        <div className="absolute top-0 -left-[10%] right-[40%] h-[40%] bg-orange-500 opacity-[0.03] blur-[120px] rounded-full"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[30%] bg-amber-500 opacity-[0.03] blur-[100px] rounded-full"></div>
      </div>

      <HeaderSection 
        scrollPosition={scrollPosition}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
      />

      <main>
        <HeroSection onSignupClick={handleSignupClick} />
        <FeatureShowcaseSection />
        <PricingSection onSignupClick={handleSignupClick} />
        <FAQsSection />
      </main>
      
      <FooterSection 
        onSignupClick={handleSignupClick}
        onShowTerms={handleShowTerms}
      />

      {!cookiesAccepted && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900/80 backdrop-blur-sm border-t border-white/10 p-4 z-50">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-gray-300 text-sm">
              We use cookies to enhance your experience and analyze site usage.
            </div>
            <div className="flex gap-3">
              <button
                onClick={acceptCookies}
                className="bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Accept Cookies
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showTerms && (
        <HousePortal>
          <TermsAndPrivacy 
            checked={false} // This was in original, seems unused for this modal instance
            onCheckChange={() => {}} // Same as above
            modalOpen={showTerms}
            setModalOpen={setShowTerms}
            initialTab={activeTermsTab}
          />
        </HousePortal>
      )}
    </div>
  );
}

export default LandingPage;